import React, { useState, useEffect } from "react";
import Loader from "../../Components/Common/Loader";
import { Col, Row, Card, CardHeader, CardBody, Button, Dropdown, DropdownItem, DropdownMenu, ButtonGroup, Input, Label, DropdownToggle, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import {
    getLocalStorageData, displayDateV1, convertDateFormate, getDateByName, getPercentageWithAlertV1,
    currencyFormatWithoutDecimalV1
} from "../../helpers/api_helper";
import { usePageApiQueue } from "../../Components/Hooks/PageApiQueue";

const CAPTURE_RATIO_API_PRIORITY = {
  WeeklyPerformance: 3,
};

const WeeklyPerformance = ({ leftFilter, rightFilter, leftCustomDate, comparisonDate }) => {
    const trendOptions = [
        { label: "30d", value: 30 },
        { label: "60d", value: 60 },
        { label: "90d", value: 90 },
    ];
    const [selectedTrend, setSelectedTrend] = useState(30);
    const [loading, setLoading] = useState(false);
    const [weeklyPerformance, setWeeklyPerformance] = useState([]);
    const { enqueue } = usePageApiQueue();

    useEffect(() => {
        if (
            (leftFilter.value !== "Custom" ||
                (leftFilter.value === "Custom" &&
                    leftCustomDate[0] &&
                    leftCustomDate[1])) &&
            (rightFilter.value !== "Custom" ||
                (rightFilter.value === "Custom" &&
                    comparisonDate[0] &&
                    comparisonDate[1]))
        ) {
            let leftFilterData =
                leftFilter.value === "Custom"
                    ? {
                        from: convertDateFormate(leftCustomDate[0]),
                        to: convertDateFormate(leftCustomDate[1]),
                    }
                    : getDateByName(leftFilter.value);
            let rightFilterData =
                rightFilter.value === "Custom"
                    ? {
                        from: convertDateFormate(comparisonDate[0]),
                        to: convertDateFormate(comparisonDate[1]),
                    }
                    : getDateByName(rightFilter.value);

            setLoading(true);
            return enqueue(
                "CaptureRatioWeeklyPerformance",
                CAPTURE_RATIO_API_PRIORITY.WeeklyPerformance,
                async (signal) => {
                    try {
                        const response = await postDataAPI({
                            serviceName: "pms_data",
                            op: "pms_weekly_performance_analysis",
                            leftFilter: leftFilterData,
                            rightFilter: rightFilterData,
                            trend_period: selectedTrend
                        }, signal);
                        if (signal.aborted) return;
                        setLoading(false);
                        if (response.status === "ok") {
                            setWeeklyPerformance(response.data);
                        }
                    } catch (e) {
                        if (!signal.aborted) setLoading(false);
                    }
                }
            );
        }
    }, [leftFilter, rightFilter, leftCustomDate, comparisonDate, selectedTrend, enqueue]);

    const getHeatmapClass = (value) => {
        if (value >= 90) return "heat-green";
        if (value >= 80) return "heat-yellow";
        if (value >= 70) return "heat-orange";
        return "heat-red";
    };

    const buildColorScale = (data) => {
        const allValues = data.map(item => item.data[0]).filter(v => v !== null && !isNaN(v));
        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        const range = max - min || 1;
        const steps = 5;
        const stepSize = range / steps;

        const colors = [
            '#F8696B',  // Very Low
            '#FA8A72',  // Low
            '#FECB7E',  // Medium
            '#8AC97D',  // High
            '#63BE7B'   // Very High
        ];

        return (val) => {
            if (val == null || isNaN(val)) return '#ccc';
            const index = Math.floor((val - min) / stepSize);
            return colors[Math.min(index, colors.length - 1)];
        };
    };

    const getColorFromRange = buildColorScale(weeklyPerformance);


    return (
        <>
            <Row>
                <Card>
                    <CardHeader className="ps-0 border-0 d-flex justify-content-between align-items-center flex-wrap">
                        <div className="d-flex align-items-start">
                            <div className={`icon-box rounded bg-danger text-white`}>
                                <i className="ri-calendar-2-fill"></i>
                            </div>
                            <div className="ps-2">
                                <h4 className="card-title mb-1">Weekly Performance Analysis</h4>
                                <p className="text-muted mb-0">
                                    Daily performance metrics comparing capture ratios against historical data and budget targets
                                </p></div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted me-2">Trend Period:</span>
                            {trendOptions.map(option => (
                                <Button
                                    key={option.value}
                                    className="px-3"
                                    color={selectedTrend === option.value ? "danger" : "outline-dark"}
                                    onClick={() => setSelectedTrend(option.value)}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </CardHeader>

                    <CardBody className="p-0">
                        {loading && <Loader />}
                        <div className={`table-responsive ${loading ? "opacity-0" : ""}`}>
                            <table className="table table-hover align-middle table-nowrap mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Day</th>
                                        <th scope="col" className="text-center">Occupancy</th>
                                        <th scope="col" className="text-center">Capture Ratio</th>
                                        <th scope="col" className="text-center">CRLY</th>
                                        <th scope="col" className="text-center">CRBudget</th>
                                        <th scope="col" className="text-center">CRTrend</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {weeklyPerformance && weeklyPerformance.map((item, idx) => (
                                        <tr key={idx}>
                                            <th scope="row">{item.name}</th>
                                            {/* {item.data.map((val, i) => (
                            <td className="text-center" key={i}>
                                {val !== null ? `${val}%` : "--"}
                            </td>
                            ))} */}
                                            {item.data.map((val, i) => {
                                                // For index 1, apply conditional heat styling
                                                if (i === 0) {
                                                    const bgColor = val !== null ? getColorFromRange(val) : "";
                                                    return (
                                                        <td className="text-center" key={i}>
                                                            <span
                                                                className="heat-box"
                                                                style={{ backgroundColor: bgColor }}
                                                            >
                                                                {val !== null ? `${val}%` : "--"}
                                                            </span>
                                                        </td>
                                                    );
                                                }
                                                if (i === 1) {
                                                    const comparisonVal = item.data[4]; // index-4 value
                                                    const currentVal = item.data[1];    // index-1 value

                                                    if (val !== null) {
                                                        const isGreen = comparisonVal < currentVal;
                                                        const heatClass = isGreen ? "heat-green" : "heat-red";

                                                        return (
                                                            <td className="text-center" key={i}>
                                                                <span className={`heat-box ${heatClass}`}>
                                                                    {`${val}%`}
                                                                </span>
                                                            </td>
                                                        );
                                                    } else {
                                                        return (
                                                            <td className="text-center" key={i}>
                                                                <span className="heat-box">--</span>
                                                            </td>
                                                        );
                                                    }
                                                }

                                                // For all other indexes
                                                return (
                                                    <td className="text-center" key={i}>
                                                        {val !== null ? `${val}%` : "--"}
                                                    </td>
                                                );
                                            })}

                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                        </div>
                    </CardBody>
                </Card>
            </Row>
        </>
    );
};

export default WeeklyPerformance;
